import { useEffect, useRef, useState } from "react";
import { sendInfoWanrSeenCart } from "~/feature/cart/cart/api/sendInfoWanrSeenCart";
import { type RootState } from "../redux/store";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCartEntries,
  selectPackageEntryMap,
  selectTotalCartPrice,
  selectTotalCartPriceWithoutDiscount,
  selectTotalCartWeight,
} from "~/feature/cart/cart/redux/selectors/cartSelectors";
import { type GroupedCart, type GroupedCartEntry } from "~/feature/cart/cart/models/CartData";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import Spinner from "../common/components/Spinner/Spinner";
import ProductTable from "~/feature/cart/cart/components/CartPage/ProductTable/ProductTable";
import Spacer from "../common/components/Spacer";
import OrderTotals from "~/feature/cart/cart/components/CartPage/OrderTotals";
import FullscreenLoader from "../common/components/FullscreenLoader";
import ToastMessage from "../common/components/Messages/ToastMessage/ToastMessage";
import { PunchoutCancelRequisition, PunchoutPlaceRequisition } from "~/feature/cart/cart/Punchout/PunchoutButtons";
import { useBooleanState } from "../common/hooks/useBooleanState";
import { CART_PNA_THRESHOLD } from "../utils/constants";
import { CheckoutButton } from "~/feature/cart/cart/components/CartPage/Buttons/CheckoutButton";
import { deleteSectionSaga } from "~/feature/cart/cart/redux/actions/cartQuoteCommonActions";
import CheckMessagesComponent from "../common/components/CheckMessagesComponent";
import DisclaimerCountryOrigin from "../common/components/Messages/DisclaimerCountryOrigin";
import { selectCart } from "~/feature/cart/cart/redux/selectors/groupedCartSelectors";
import { IncludesSurchargeDisclaimer } from "../common/components/Messages/IncludesSurchargeDisclaimer";
import { type ProductEntryListRef } from "~/feature/cart/cart/components/CartPage/ProductTable/ProductEntryList/ProductEntryList";
import { useTranslation } from "react-i18next";
import { useCartPageContext } from "~/feature/cart/cart/CartPageContext";
import { IsBundledQuoteContextProvider } from "~/feature/cart/cart/IsBundledQuoteContext";
import {
  selectCartContainsSurcharge,
  selectCartContainsTariffSurcharge,
  selectCartProductAmount,
  selectCartSurcharge,
} from "~/feature/cart/cart/redux/selectors/selectors";
import { Stacker } from "~/common/components/Stacker/Stacker";
import {
  selectAccountNumber,
  selectCartContainsHousingKIT,
  selectDisableCheckout,
  selectIsDstError,
  selectIsPnaLoading,
  selectIsRequestInFlight,
} from "~/feature/cart/cart/redux/selectors/cartPageSelectors";
import { isPunchoutEnabled } from "~/feature/cart/cart/Punchout/punchoutConstants";
import { selectProductDescriptor } from "~/redux/products/productsSelectors";
import { Alert } from "~/common/m3/Alert/Alert";
import {
  switchProductVersion,
  updatePriceReferenceForCart as updatePriceReference,
} from "~/feature/cart/cart/redux/actions/cartPageActions";
import { Snackbar, SnackbarCloseButton } from "~/common/m3/Snackbar/Snackbar";
import { CartQuickActionsButton } from "~/feature/cart/cart/components/CartPage/Buttons/CartQuickActionsButton";
import { useAlternateProducts } from "~/feature/cart/cart/AlternateProductsContext";
import { Stack } from "@mui/material";
import CheckoutSectionTitle from "~/feature/cart/checkout/components/CheckoutSectionTitle";

/**
 * This variant of the cart page is used by:
 *
 * - Punchout
 * - B2B aka "Customers"
 */
export const B2BCartPage = () => {
  const { t } = useTranslation();
  const { recentlyAddedItem, setRecentlyAddedItem, scrollIndex, setScrollIndex } = useAlternateProducts();

  const cartData = useSelector(selectCart);
  const { disableCheckout: externalDisableCheckout } = useCartPageContext();
  const disableCheckout = useSelector(selectDisableCheckout(externalDisableCheckout));
  const requestInFlight = useSelector(selectIsRequestInFlight);

  const accountNumber = useSelector(selectAccountNumber);

  const productAmount = useSelector(selectCartProductAmount);
  const isPnaLoading = useSelector(selectIsPnaLoading);

  const totalWeight = useSelector(selectTotalCartWeight);
  const totalSum = useSelector(selectTotalCartPrice);
  const totalDiscount = useSelector(selectTotalCartPriceWithoutDiscount);
  const containsHousingKits = useSelector(selectCartContainsHousingKIT);

  const showPnALoading = isPnaLoading && productAmount <= CART_PNA_THRESHOLD;

  const scrollToRefs = useRef<Dict<ProductEntryListRef>>({});

  const invalidItemIndexes = useSelector((state: RootState) => getInvalidItemData(cartData, state));
  const invalidLineItemAmount = invalidItemIndexes.length;

  const webservicesDisabled = window.SCHA.config.disableAllWS;

  const [snackbarData, setSnackbarData] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  const dispatch = useDispatch();
  const deleteSection = (quoteNumber?: string) => {
    dispatch(deleteSectionSaga(quoteNumber || ""));
  };

  const hasAnySurcharge = useSelector(selectCartContainsSurcharge);
  const hasAnyTariffSurcharge = useSelector(selectCartContainsTariffSurcharge);
  const surchargeValue = useSelector(selectCartSurcharge);

  const packageEntryMap = useSelector(selectPackageEntryMap);
  const packageEntryIds = Object.keys(packageEntryMap).map(Number);
  const cartEntries = useSelector(selectCartEntries);
  const updatePriceReferenceForCart = (priceReference: string) =>
    dispatch(updatePriceReference(priceReference, packageEntryIds, handleOpenPriceRefSnackBar));
  const switchProductVersionForCart = () => dispatch(switchProductVersion());

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const snackbarValue = urlParams.get("switched-products-count");

    if (snackbarValue) {
      handleOpenSwitchProductSnackBar(parseInt(snackbarValue));
      removeUrlParam("switched-products-count");
    }
  }, []);

  function getcartEntryIdByCode(productCode: string) {
    const entries = Object.values(packageEntryMap);
    return entries.find((product) => product.productCode === productCode)?.cartEntryId;
  }

  useEffect(() => {
    if (recentlyAddedItem) {
      setSnackbarData({
        open: true,
        message: t("cartPage.addProduct.success.snackbar"),
      });

      const cartEntryID = getcartEntryIdByCode(recentlyAddedItem.code);
      const index = cartEntries.findIndex((item) => item === cartEntryID);

      const scrollRef = scrollToRefs.current["orderables"];
      scrollRef.scrollToIndex(index);

      const timer = setTimeout(() => {
        setRecentlyAddedItem(null);
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [recentlyAddedItem, cartData.orderables.entries.length]);

  const removeUrlParam = (key: string) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    window.history.replaceState({}, "", url);
  };

  const handleOpenPriceRefSnackBar = (count: number) => {
    setSnackbarData({
      open: true,
      message: t("cartPage.modal.choosePriceReferenceForCart.snackbar", { count }),
    });
  };

  const handleOpenSwitchProductSnackBar = (count: number) => {
    setSnackbarData({
      open: true,
      message: t("cartPage.modal.switchProductVersion.snackbar", { count }),
    });
  };

  const checkOutBtnSection = (
    <>
      {!isPunchoutEnabled() && <CheckoutButton disabled={disableCheckout} />}
      {isPunchoutEnabled() && (
        <>
          <PunchoutCancelRequisition />
          <PunchoutPlaceRequisition disabled={disableCheckout} />
        </>
      )}
    </>
  );

  const ButtonSection = (
    <Stacker gap={8} direction={"row"}>
      <CartQuickActionsButton
        disableReportDownload={isPnaLoading}
        switchProductVersionForCart={switchProductVersionForCart}
        updatePriceReferenceForCart={updatePriceReferenceForCart}
      />
      {checkOutBtnSection}
    </Stacker>
  );

  return (
    <>
      <DstErrorMessage />
      <PageTitle tag="div">
        <div style={{ marginBottom: "16px" }} className="d-flex flex-justify-between flex-align-center">
          <div>{t("cartPage.title", { accNo: accountNumber })}</div>
          <div data-test-id={"cart-action-bar"}>{ButtonSection}</div>
        </div>
      </PageTitle>

      <Snackbar
        open={snackbarData.open}
        message={snackbarData.message}
        onClose={() => {
          setSnackbarData((prev) => ({ ...prev, open: false }));
          setRecentlyAddedItem(null);
        }}
        action={<SnackbarCloseButton onClick={() => setSnackbarData((prev) => ({ ...prev, open: false }))} />}
      />

      {showPnALoading && <Spinner className={"mb-2"} />}
      {invalidLineItemAmount > 0 && (
        <CheckMessagesComponent
          data-test-id={"cart-issue-with-product"}
          msgType={"error"}
          msgCount={invalidLineItemAmount}
          onScrollToMessage={(index) => {
            const data = invalidItemIndexes[index];
            scrollToRefs.current![data.tableName].scrollToIndex(data.index);
          }}
          sticky={true}
        />
      )}

      <ProductTable
        group={cartData.orderables}
        editMode={true}
        onClear={deleteSection}
        data-test-id={"cart-section-regular"}
        scrollRef={(ref) => (scrollToRefs.current!["orderables"] = ref!)}
      />

      {cartData.bundles.map((bundle, index) => (
        <IsBundledQuoteContextProvider value={true}>
          <Spacer isThick={true} className={"mt-30 mb-60"} />
          <CheckoutSectionTitle>
            {t("page.cart.bundledQuote.title", { amount: bundle.entries.length, quoteNumber: bundle.quoteNumber })}
          </CheckoutSectionTitle>
          <p className="mt-2 mb-3">{t("page.cart.bundledQuote.subtitle")}</p>
          <ProductTable
            group={bundle}
            editMode={true}
            onClear={() => deleteSection(bundle.quoteNumber)}
            data-test-id={`cart-section-bundle-${bundle.quoteNumber}`}
            scrollRef={(ref) => (scrollToRefs.current![index] = ref!)}
          />
        </IsBundledQuoteContextProvider>
      ))}

      <Spacer isThick={true} className={"mt-30 mb-30"} />
      <div className="row mb-30">
        <div className="col-4 col-push-8">
          <OrderTotals
            showPriceOnRequest={true}
            weight={totalWeight}
            totalValue={webservicesDisabled ? null : totalSum}
            surcharge={hasAnySurcharge && totalSum && totalSum.value > 0 ? surchargeValue : undefined}
            tariffSurcharge={hasAnyTariffSurcharge}
            totalDiscount={totalDiscount}
          >
            {checkOutBtnSection}
          </OrderTotals>
        </div>
      </div>
      <Stack gap={2}>
        <Alert severity="info">
          <div data-test-id={"info-disclaimer"}>{t("cartPage.disclaimer")}</div>
        </Alert>
        <DisclaimerCountryOrigin />
        {containsHousingKits && (
          <Stack gap={2}>
            <Alert severity="info">
              <div data-test-id={"info-housing-disclaimer-1"}>{t("cartPage.housingDisclaimer.1")}</div>
            </Alert>
            <Alert severity="info">
              <div data-test-id={"info-housing-disclaimer-2"}>{t("cartPage.housingDisclaimer.2")}</div>
            </Alert>
          </Stack>
        )}
        {<IncludesSurchargeDisclaimer hasSurcharge={hasAnySurcharge} tariffSurcharge={hasAnyTariffSurcharge} />}
      </Stack>

      {requestInFlight && <FullscreenLoader />}
    </>
  );
};

const buttonContainerStyleWithErrors: React.CSSProperties = { height: "50px", marginTop: "-51px" };
const buttonContainerStyleWithoutErrors: React.CSSProperties = { height: "50px", marginTop: "30px" };

const DstErrorMessage = () => {
  const { t } = useTranslation();
  const { val, setTrue, setFalse } = useBooleanState(false);
  const dstError = useSelector(selectIsDstError);
  useEffect(() => {
    if (dstError) {
      setTrue();
      sendInfoWanrSeenCart();
    }
  }, [dstError]);

  return (
    <>
      {val && (
        <ToastMessage type={"warning"} onClose={() => setFalse()}>
          {t("cartPage.warnMsg.dst")}
        </ToastMessage>
      )}
    </>
  );
};

const getInvalidItemData = (cartData: GroupedCart, state: RootState) => {
  const invalidEntries: { tableName: string; index: number }[] = [];
  const makeEntryProcessor = (tableName: string) => (entry: GroupedCartEntry, index: number) => {
    if (
      entry.packGroups.length > 0 &&
      entry.packGroups.some(
        (pack) =>
          pack.packEntryIds.length > 0 &&
          pack.packEntryIds.some((id) => {
            const lineItem = state.cart.packEntriesById[id];
            const productDescriptor = selectProductDescriptor(state, lineItem);
            return !lineItem.valid || productDescriptor.isError;
          }),
      )
    ) {
      invalidEntries.push({ tableName, index });
    }
  };
  cartData.orderables.entries.forEach(makeEntryProcessor("orderables"));
  cartData.bundles.forEach((bundle, bundleIndex) => bundle.entries.forEach(makeEntryProcessor("" + bundleIndex)));
  return invalidEntries;
};
